import { FlexContainer, LoadingPage } from '@act/storybook';
import type { CreateReportRequestDto } from '@act/shared/data-transfer-objects';
import { FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportMetadataFormFields from '../components/ReportMetadataFormFields';
import ReportsPageHeader from '../components/ReportsPageHeader';
import ReportsUnauthorized from '../components/ReportsUnauthorized';
import { useReportMetadataForm } from '../hooks/useReportMetadataForm.hook';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';

/**
 * Form for creating a new report (customer, measurement period, optional template).
 */
const NewReportPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { canEdit } = useReportsPermissions();

  const form = useReportMetadataForm({ mode: 'create' });

  const [createdReport, creating, createReport] = useApiCall(
    reportsRequests.createReport,
  );

  useEffect(() => {
    if (createdReport && !creating) {
      toast('Report created', 'success');
      navigate(`/${createdReport.id}`);
    }
  }, [createdReport, creating, navigate, toast]);

  if (!canEdit) {
    return <ReportsUnauthorized />;
  }

  if (
    form.customersLoading !== false ||
    form.templatesLoading !== false
  ) {
    return <LoadingPage />;
  }

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!form.canSubmit || creating) {
      return;
    }

    const body: CreateReportRequestDto = {
      name: form.name.trim(),
      customerId: form.customerId,
      cadence: form.cadence,
      reportingPeriod: form.reportingPeriodLabel,
      ...(form.cadence === 'adhoc'
        ? { measurementPeriod: form.measurementPeriod.trim() }
        : form.measurementPeriod.trim()
          ? { measurementPeriod: form.measurementPeriod.trim() }
          : {}),
      ...(form.templateId ? { createdFromTemplateId: form.templateId } : {}),
    };

    createReport({ body });
  };

  return (
    <FlexContainer
      component="form"
      direction="column"
      gap={2}
      width="100%"
      height="auto"
      overflowAllVisible={true}
      onSubmit={handleSubmit}
    >
      <ReportsPageHeader
        title="New report"
        subtitle="Start a new report with default section rows for the selected customer."
      />

      <ReportMetadataFormFields
        mode="create"
        name={form.name}
        nameTouched={form.nameTouched}
        customerId={form.customerId}
        cadence={form.cadence}
        cadenceLabel={form.cadenceLabel}
        measurementPeriod={form.measurementPeriod}
        endMonthKey={form.endMonthKey}
        templateId={form.templateId}
        customers={form.customers}
        templates={form.templates}
        measurementPeriodOptions={form.measurementPeriodOptions}
        reportingMonthOptions={form.reportingMonthOptions}
        measurementPeriodsLoading={form.measurementPeriodsLoading}
        reportingMonthsLoading={form.reportingMonthsLoading}
        measurementPeriodMatchStatus={form.measurementPeriodMatchStatus}
        autoMatchedMeasurementPeriodLabel={form.autoMatchedMeasurementPeriodLabel}
        defaultReportName={form.defaultReportName}
        submitting={creating}
        canSubmit={form.canSubmit}
        onNameChange={form.handleNameChange}
        onNameFocus={form.handleNameFocus}
        onResetNameToDefault={form.handleResetNameToDefault}
        onCustomerChange={form.handleCustomerChange}
        onCadenceChange={form.handleCadenceChange}
        onMeasurementPeriodChange={form.handleMeasurementPeriodChange}
        onEndMonthKeyChange={form.handleEndMonthKeyChange}
        onTemplateChange={form.handleTemplateChange}
        onSubmit={handleSubmit}
        submitLabel={creating ? 'Creating…' : 'Create report'}
      />
    </FlexContainer>
  );
};

export default NewReportPage;
